import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormRow } from '@gravity-ui/components';
import { Pencil, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Modal, Sheet, TextInput, Tooltip } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAddEstablishmentMutation } from 'store/api';

import { CANCEL, EDIT_ACC_ESTABLISHMENT, ESTABLISHMENT, SAVE } from '../../constants';
import type { EstablishmentAddForm } from '../utils/useValidation';
import { useEstablishmentAddValidation } from '../utils/useValidation';
import { toast } from 'react-toastify';

import styles from './styles.module.scss';

interface Establishment {
	id?: number;
	name: string;
}
interface EstablishmentEditModalProps {
	establishment: Establishment;
	onAdd: () => void;
}

export const EstablishmentEditSheet = ({ establishment, onAdd }: EstablishmentEditModalProps) => {
	const [open, setOpen] = useState(false);
	const { establishmentAddSchema } = useEstablishmentAddValidation();

	const [addEstablishment] = useAddEstablishmentMutation();

	const methods = useForm<EstablishmentAddForm>({
		resolver: zodResolver(establishmentAddSchema),
		defaultValues: {
			name: establishment?.name || '',
		},
	});

	const {
		handleSubmit,
		register,
		reset,
		formState: { errors, isSubmitted, isValid },
	} = methods;

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	const onSubmit = async (data: EstablishmentAddForm) => {
		const payload = {
			...data,
			id: establishment.id,
		};
		await addEstablishment(payload).unwrap();
		onAdd();

		setOpen(false);

		toast.success('Учреждение успешно изменено');
	};
	return (
		<>
			<Button view="flat" size="l" onClick={() => setOpen(true)}>
				<Icon data={Pencil} size={16} />
			</Button>

			<Sheet
				visible={open}
				onClose={() => setOpen(false)}
				className={styles.sheet}
				title={EDIT_ACC_ESTABLISHMENT}
			>
				<div className={styles.sheet__close}>
					<Button onClick={() => setOpen(false)} view="flat">
						<Icon data={Xmark} size={16} />
					</Button>
				</div>

				<FormProvider {...methods}>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className={styles.modal__rows}>
							<FormRow className={styles.modal__row} label={ESTABLISHMENT} required>
								<TextInput
									size="l"
									{...register('name', { required: true })}
									validationState={isSubmitted && errors.name ? 'invalid' : undefined}
									errorPlacement="inside"
									errorMessage={isSubmitted && errors.name ? 'Обязательно для заполнения' : undefined}
								/>
							</FormRow>
						</div>

						<div className={styles.modal__btns}>
							<Button size="xl" view="flat-secondary" onClick={() => setOpen(false)}>
								{CANCEL}
							</Button>
							<Tooltip content="Отредактируйте название" openDelay={100} placement="top">
								<div tabIndex={0}>
									<Button size="xl" type="submit" className={styles.modal__btns__save}>
										{SAVE}
									</Button>
								</div>
							</Tooltip>
						</div>
					</form>
				</FormProvider>
			</Sheet>
		</>
	);
};
